import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import FastForwardRoundedIcon from '@mui/icons-material/FastForwardRounded';
import FastRewindRoundedIcon from '@mui/icons-material/FastRewindRounded';

export default function CustomVideoPlayer({
  src,
  poster,
  accentColor = "#3b82f6",
  isSystemMessage = false,
  autoPlay = false,
}) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (autoPlay) {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }

    const onLoaded = () => setDuration(v.duration || 0);
    const onTime = () => setCurrentTime(v.currentTime || 0);
    const onEnded = () => setIsPlaying(false);

    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("ended", onEnded);

    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("ended", onEnded);
      try {
        v.pause();
      } catch (_) {}
    };
  }, [autoPlay]);

  const formatTime = (timeInSec) => {
    if (!isFinite(timeInSec)) return "0:00";
    const minutes = Math.floor(timeInSec / 60);
    const seconds = Math.floor(timeInSec % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      v.pause();
      setIsPlaying(false);
    } else {
      const p = v.play();
      if (p && p.catch) {
        p.catch(() => {});
      }
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const onProgressClick = (e) => {
    const bar = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bar.left;
    const pct = Math.max(0, Math.min(1, x / bar.width));
    const v = videoRef.current;
    if (!v || !isFinite(duration) || duration === 0) return;
    v.currentTime = pct * duration;
    setCurrentTime(v.currentTime);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };
    useEffect(() => {
        const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () => {
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, []);

  const progress = duration ? Math.min(1, currentTime / duration) : 0;

  const textColor = "white";
  const accent = isSystemMessage ? "rgba(255,255,255,0.92)" : accentColor;
  const inactive = isSystemMessage ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.12)";
  const iconColor = "white";

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        maxHeight: isFullscreen ? "100vh" : 300,
        position: "relative",
        borderRadius: isFullscreen ? 0 : 2,
        overflow: "hidden",
        bgcolor: isSystemMessage ? "rgba(255,255,255,0.06)" : "black",
      }}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          background: "black",
        }}
        onClick={togglePlay}
      />

      {/* Centered Play/Pause with Forward/Rewind */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          alignItems: "center",
          gap: 4,
          p: 1.5,
          background:'transparent',
          borderRadius: 2,
          backdropFilter: "blur(8px)",
        }}
      >
        <IconButton
          onClick={() => {
            const v = videoRef.current;
            if (v) v.currentTime = Math.max(0, v.currentTime - 5);
          }}
          sx={{ color: iconColor }}
        >
          <FastRewindRoundedIcon sx={{ fontSize: 32 }} />
        </IconButton>

        <IconButton
          onClick={togglePlay}
          sx={{
            background:'transparent',
            color: "white",
            width: 64,
            height: 64,
            '&:hover': { transform: 'scale(1.08)' },
          }}
        >
          {isPlaying ? <PauseRoundedIcon sx={{ fontSize: 32 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 32 }} />}
        </IconButton>

        <IconButton
          onClick={() => {
            const v = videoRef.current;
            if (v) v.currentTime = Math.min(duration, v.currentTime + 5);
          }}
          sx={{ color: iconColor }}
        >
          <FastForwardRoundedIcon sx={{ fontSize: 32 }} />
        </IconButton>
      </Box>

      {/* Bottom Right Controls */}
      <Box
        sx={{
          position: "absolute",
          bottom: 8,
          right: 8,
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        <IconButton onClick={toggleMute} sx={{ color: iconColor }}>
          {muted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
        </IconButton>

        <IconButton onClick={toggleFullscreen} sx={{ color: iconColor }}>
          {isFullscreen ? <FullscreenExitRoundedIcon /> : <FullscreenRoundedIcon />}
        </IconButton>
      </Box>

      {/* Progress Bar */}
      <Box
        onClick={onProgressClick}
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 6,
          bgcolor: inactive,
          cursor: "pointer",
        }}
      >
        <Box sx={{ width: `${progress * 100}%`, height: "100%", bgcolor: accent }} />
      </Box>
    </Box>
  );
}
